import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation, useParams, useSearchParams } from "wouter";
import * as Tabs from "@radix-ui/react-tabs";
import { keyBy } from "lodash";

import { AttributionGraph } from "./Attribution";
import { PerformanceTree } from "./PerformanceTree";
import { PerformanceOverview } from "./PerformanceOverview";

import { DateHelper, emptyObjectId } from "../../../../common/src";
import { useQueryState } from "../../../../components/src/use-query-state";
import { CurrencyEnum, Party, PartyStatusEnum, PartyType } from "../../types.generated";
import { useQuery } from "urql";
import { getClients, getPerformanceData } from "../../../../graphql/src/performance/queries";
import { ClientContextSelector } from "../../contexts/ClientContextSelector";
import { ClientContext } from "../../../../components/src/contexts/ClientContext";
import { ResponsiveTabsList } from "../../../../components/src/ResponsiveTabsList";

import { PerformanceTabSimple } from "./PerformanceTabSimple";
import { PerformanceSectors } from "./PerformanceSectors";

const tabs = {
    overview: "overview",
    attribution: "attribution",
    tree: "tree",
    simple: "simple",
    "pivot-data": "pivot-data"
};

export function PerformancePage(): React.ReactElement {
    const { tabId } = useParams<{ tabId: string }>();
    const [, navigate] = useLocation();
    const [queryArgs] = useSearchParams();
    const { client: clientContext } = useContext(ClientContext);

    // The selected period lives in the query string, so tab navigation has to carry it over — otherwise
    // switching tab resets the dates to their defaults. Only the period is carried: the tabs also put
    // their own state in the query string (Attribution its plot/selector/filters, Simple its print and
    // pie-chart flags), and those are meaningless on the other tabs, so they are dropped on tab change
    // and re-seeded by the target tab itself.
    const querySuffix: string = useMemo(() => {
        const carried = new URLSearchParams();
        for (const key of ["startDate", "endDate"]) {
            const value = queryArgs.get(key);
            if (value) {
                carried.set(key, value);
            }
        }
        const search = carried.toString();
        return search ? `?${search}` : "";
    }, [queryArgs]);

    const yesterday: string = DateHelper.dateToString(DateHelper.previousWeekday());

    const [endDate, setEndDate] = useQueryState("endDate", yesterday);

    const [{ data, fetching: loading, error }] = useQuery<{ clients?: Partial<Party>[] }>({
        query: getClients,
        requestPolicy: "cache-and-network"
    });

    const {
        client,
        clientsByDashName
    }: { client: Partial<Party>; clientsByDashName: Record<string, Partial<Party>> } = useMemo(() => {
        let client = null;
        let clientsByDashName = {};
        if (data) {
            const clientsById = keyBy(data.clients, "_id");
            clientsByDashName = keyBy(data.clients, (client) => {
                return client.name.replace(/ /g, "-");
            });
            client = clientsById[clientContext._id] || null;
        }
        return { client, clientsByDashName };
    }, [data, clientContext]);

    const [{ data: dataPerformance, error: errorPerformance }] = useQuery({
        query: getPerformanceData,
        variables: {
            clientId: client?._id,
            endDate: endDate,
            endDateString: endDate,
            accountingCurrency: client?.accountingCurrency ? (client.accountingCurrency as CurrencyEnum) : CurrencyEnum.SEK
        },
        requestPolicy: "cache-and-network",
        pause: !client?._id
    });

    // A payload belonging to the currently selected client is rendered straight away — including a
    // cached one for a client seen earlier this session — so switching between already-loaded clients is
    // instant. `cache-and-network` still refetches over the network in the background, and once the
    // fresh payload arrives the tab components rebuild their derived state from it (they reset on data
    // identity changes), so any outdated cached numbers correct themselves automatically.
    //
    // The last matching payload is also kept in a ref so background refetches with new query variables
    // (e.g. when the user changes the end date in the toolbar) do not unmount the tab. The ref is
    // dropped when the selected client changes, so the previous client's numbers are never shown for the
    // new one — in that case the full-screen Loading state is shown until the new client's (cached or
    // fresh) payload is available.
    const lastPerformanceRef = useRef<typeof dataPerformance>(undefined);
    const lastClientIdRef = useRef<string | null>(null);
    if (client?._id && client._id !== lastClientIdRef.current) {
        lastClientIdRef.current = client._id;
        lastPerformanceRef.current = undefined;
    }
    const performanceMatchesClient = Boolean(dataPerformance && client?._id && dataPerformance.client?._id === client._id);
    if (performanceMatchesClient) {
        lastPerformanceRef.current = dataPerformance;
    }
    const effectivePerformanceData = performanceMatchesClient ? dataPerformance : lastPerformanceRef.current;

    useEffect(() => {
        if (data && data.clients) {
            if (clientContext.dashName && clientContext._id !== emptyObjectId) {
                if (!(clientContext.dashName in clientsByDashName)) {
                    navigate("/performance");
                } else {
                    if (!(tabId in tabs)) {
                        navigate("/performance/" + clientContext.dashName + "/overview" + querySuffix);
                    }
                }
            }
        }
    }, [clientContext._id, clientContext.dashName, clientsByDashName, data, navigate, querySuffix, tabId]);

    const onTabChange = useCallback(
        (tabId: string) => {
            if (!(tabId in tabs)) {
                navigate(`/performance/${clientContext.dashName}/overview${querySuffix}`);
            } else {
                navigate(`/performance/${clientContext.dashName}/${tabId}${querySuffix}`);
            }
        },
        [clientContext.dashName, navigate, querySuffix]
    );

    if (loading) {
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );
    }

    // Show the full-screen loading state only while no payload at all is available for the currently
    // selected client, i.e. the initial load and switches to a client not seen this session. Switches
    // back to an already-loaded client render the in-memory payload immediately while it refreshes in
    // the background.
    if (!effectivePerformanceData && client?._id && !errorPerformance) {
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );
    }

    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (errorPerformance)
        return (
            <div>
                <p>error performance:</p>
                <pre> {JSON.stringify(errorPerformance, null, 2)}</pre>
            </div>
        );

    if (!tabId) {
        <div></div>;
    }

    const clientsByName = keyBy(data.clients, "name");
    if (
        clientContext &&
        clientContext._id !== emptyObjectId &&
        !Object.keys(clientsByName).includes(clientContext.dashName.replace(/-/g, " "))
    ) {
        return (
            <div>
                <h3>No client with that name</h3>
            </div>
        );
    }

    return (
        <div id="performancepage">
            <div className="flex flex-wrap -mx-2 print-none">
                <div className="w-full sm:w-1/3 px-2 mb-4">
                    <ClientContextSelector
                        typeIn={[PartyType.Client]}
                        carryQueryArgs={false}
                        statusIn={[PartyStatusEnum.Confirmed, PartyStatusEnum.Inactive]}
                    />
                </div>
            </div>
            {clientContext.dashName && clientContext._id !== emptyObjectId ? (
                <div className="flex flex-wrap -mx-2">
                    <div className="border border-gray-300 rounded shadow p-4 bg-gray-100 w-full px-2 mb-4">
                        <Tabs.Root value={tabId || "overview"} onValueChange={onTabChange} className="w-full">
                            <ResponsiveTabsList value={tabId || "overview"} onValueChange={onTabChange}>
                                <Tabs.Trigger
                                    value="overview"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Overview
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="tree"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Detailed
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="attribution"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Attribution
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="simple"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Simple
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="pivot-data"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Pivot data
                                </Tabs.Trigger>
                            </ResponsiveTabsList>

                            <Tabs.Content value="overview" className="mt-4">
                                {tabId === "overview" && (
                                    <PerformanceOverview
                                        client={client._id ? client : clientsByName[clientContext.dashName.replace(/-/g, " ")]}
                                        data={effectivePerformanceData}
                                        endDate={endDate}
                                        setEndDate={setEndDate}
                                    />
                                )}
                            </Tabs.Content>

                            <Tabs.Content value="tree" className="mt-4">
                                {tabId === "tree" && (
                                    <PerformanceTree
                                        client={client._id ? client : clientsByName[clientContext.dashName.replace(/-/g, " ")]}
                                        data={effectivePerformanceData}
                                        endDate={endDate}
                                    />
                                )}
                            </Tabs.Content>

                            <Tabs.Content value="attribution" className="mt-4">
                                {tabId === "attribution" && (
                                    <AttributionGraph
                                        client={client._id ? client : clientsByName[clientContext.dashName.replace(/-/g, " ")]}
                                        data={effectivePerformanceData}
                                    />
                                )}
                            </Tabs.Content>

                            <Tabs.Content value="simple" className="mt-4">
                                {tabId === "simple" && (
                                    <PerformanceTabSimple
                                        client={client._id ? client : clientsByName[clientContext.dashName.replace(/-/g, " ")]}
                                        data={effectivePerformanceData}
                                        endDate={endDate}
                                    />
                                )}
                            </Tabs.Content>

                            <Tabs.Content value="pivot-data" className="mt-4">
                                {tabId === "pivot-data" && (
                                    <PerformanceSectors
                                        client={client._id ? client : clientsByName[clientContext.dashName.replace(/-/g, " ")]}
                                        data={effectivePerformanceData}
                                        endDate={endDate}
                                    />
                                )}
                            </Tabs.Content>
                        </Tabs.Root>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
